import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Clock, ArrowLeft, Users, CheckCircle2, AlertCircle, LogIn, LogOut as LogOutIcon, Activity, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDuration(seconds) {
  if (!seconds || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  });
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function endedReasonLabel(reason) {
  if (reason === "active") return { text: "Active now", color: "bg-emerald-100 text-emerald-800 border-emerald-300" };
  if (reason === "manual") return { text: "Logged out", color: "bg-gray-100 text-gray-700 border-gray-300" };
  if (reason === "auto_idle") return { text: "Auto (idle)", color: "bg-amber-100 text-amber-800 border-amber-300" };
  return { text: reason || "—", color: "bg-gray-100 text-gray-700 border-gray-300" };
}

export default function LoginsView({ onBack }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/login-sessions/groups`);
      setGroups(r.data?.groups || []);
    } catch (_e) {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    try {
      const r = await axios.get(`${API}/login-sessions/engagement-report`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `engagement-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) { /* swallow */ }
  };

  const loadSessions = async (code) => {
    setSessionsLoading(true);
    try {
      const r = await axios.get(`${API}/login-sessions/groups/${encodeURIComponent(code)}`);
      setSessions(r.data?.sessions || []);
    } catch (_e) {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => { loadGroups(); }, []);
  useEffect(() => { if (selectedGroup) loadSessions(selectedGroup.group_code); }, [selectedGroup]);

  // ── Group detail view ──
  if (selectedGroup) {
    return (
      <div className="space-y-4" data-testid="logins-group-detail">
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => setSelectedGroup(null)} data-testid="logins-back-to-groups">
            <ArrowLeft className="w-4 h-4 mr-2" /> All groups
          </Button>
          <Button variant="ghost" size="sm" onClick={() => loadSessions(selectedGroup.group_code)} data-testid="logins-refresh-sessions">
            Refresh
          </Button>
        </div>

        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border-2 border-indigo-200 p-6">
          <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-1">Login history · Last 7 days</p>
          <h2 className="text-2xl font-bold text-gray-900">{selectedGroup.group_code} · {selectedGroup.group_name}</h2>
          <div className="flex flex-wrap gap-3 mt-3 text-sm">
            <span className="inline-flex items-center gap-1.5 bg-white/70 px-3 py-1 rounded-full text-gray-700"><LogIn className="w-3.5 h-3.5" /> {selectedGroup.session_count} session{selectedGroup.session_count !== 1 ? "s" : ""}</span>
            {selectedGroup.active_count > 0 && (
              <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full font-semibold">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> {selectedGroup.active_count} active now
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 bg-white/70 px-3 py-1 rounded-full text-gray-700"><Clock className="w-3.5 h-3.5" /> Total time: {fmtDuration(selectedGroup.total_duration_seconds)}</span>
          </div>
        </div>

        {sessionsLoading ? (
          <div className="text-center py-12 text-gray-500">Loading sessions…</div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
            No login sessions in the last 7 days.
          </div>
        ) : (
          <div className="space-y-3" data-testid="logins-sessions-list">
            {sessions.map((s) => {
              const status = endedReasonLabel(s.ended_reason);
              return (
                <div key={s.id} className="bg-white rounded-2xl border-2 border-gray-200 p-5 shadow-sm hover:border-indigo-300 transition-colors" data-testid={`session-${s.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <LogIn className="w-4 h-4 text-indigo-600" />
                        <span className="font-semibold text-gray-900">{fmtDateTime(s.login_at)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5 text-sm text-gray-600">
                        {s.is_active ? (
                          <>
                            <Activity className="w-4 h-4 text-emerald-600" />
                            <span>Last activity {fmtTime(s.last_activity_at)}</span>
                          </>
                        ) : (
                          <>
                            <LogOutIcon className="w-4 h-4 text-gray-500" />
                            <span>Logged out {fmtTime(s.logout_at)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      <Badge className={`border ${status.color} font-semibold`}>{status.text}</Badge>
                      <span className="text-sm font-bold text-gray-900 inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {fmtDuration(s.duration_seconds)}</span>
                    </div>
                  </div>

                  {s.actions && s.actions.length > 0 ? (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Activity ({s.actions.length})</p>
                      <ul className="space-y-1.5">
                        {s.actions.map((a, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex gap-3">
                            <span className="text-xs text-gray-400 font-mono w-14 flex-shrink-0 pt-0.5">{fmtTime(a.at)}</span>
                            <span>{a.label}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-xs text-gray-400 italic">No actions logged in this session — counselor opened the app but didn't submit anything.</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Group list view ──
  return (
    <div className="space-y-4" data-testid="logins-groups-view">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="logins-back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to dashboard
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={downloadReport} data-testid="logins-download-report" className="gap-1.5">
            <Download className="w-4 h-4" /> Engagement report
          </Button>
          <Button variant="ghost" size="sm" onClick={loadGroups} data-testid="logins-refresh-groups">
            Refresh
          </Button>
        </div>
      </div>

      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border-2 border-indigo-200 p-6">
        <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-1">Counselor activity · Last 7 days</p>
        <h2 className="text-2xl font-bold text-gray-900">Group Logins</h2>
        <p className="text-sm text-gray-600 mt-1">Tap a group to see when the head counselor logged in, how long they stayed, and what they did.</p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 text-gray-500 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
          <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-semibold">No counselor logins in the last 7 days.</p>
          <p className="text-sm mt-1">Sessions will appear here once head counselors start using the app.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="logins-groups-grid">
          {groups.map((g) => (
            <button
              key={g.group_code}
              type="button"
              onClick={() => setSelectedGroup(g)}
              className="text-left bg-white rounded-2xl border-2 border-gray-200 p-4 shadow-sm hover:border-indigo-400 hover:shadow-md transition-all"
              data-testid={`logins-group-card-${g.group_code}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-lg font-bold text-gray-900">{g.group_code}</span>
                {g.active_count > 0 ? (
                  <span className="inline-flex items-center gap-1 text-xs font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                    Active
                  </span>
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-gray-300" />
                )}
              </div>
              <p className="text-sm text-gray-600 font-medium truncate">{g.group_name || "—"}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-indigo-50 rounded-lg px-2 py-1.5">
                  <p className="text-indigo-700 font-semibold">{g.session_count}</p>
                  <p className="text-gray-500">login{g.session_count !== 1 ? "s" : ""}</p>
                </div>
                <div className="bg-purple-50 rounded-lg px-2 py-1.5">
                  <p className="text-purple-700 font-semibold">{fmtDuration(g.total_duration_seconds)}</p>
                  <p className="text-gray-500">total time</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">Last: {fmtDateTime(g.last_login_at)}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
